import cn from 'classnames';
import 'flag-icons/css/flag-icons.min.css';
import { Avatar, Badge, FlagIcon, Skeleton, lucideIcons } from '@round/creatorbase-ui';
import { ConfigurationItem, ConfigurationItemIcon, ConfigurationItemKind } from '../../helpers';
import styles from './ConfigurationItemBadge.module.css';

/** A sound is left out: its cover art leads instead, and the note below stands in when there is none. */
const ITEM_ICONS: Record<Exclude<ConfigurationItemIcon, 'sound'>, lucideIcons.LucideIcon> = {
    username: lucideIcons.AtSign,
    link: lucideIcons.Link,
    email: lucideIcons.Mail,
    location: lucideIcons.MapPin,
    rate: lucideIcons.DollarSign,
};

/**
 * Filled for what the request holds, outlined for what it was narrowed by — so a glance down the
 * section tells the values apart from the settings that produced them.
 */
const KIND_VARIANTS: Record<ConfigurationItemKind, 'secondary' | 'outline'> = {
    target: 'secondary',
    filter: 'outline',
    output: 'secondary',
};

type Props = {
    item: ConfigurationItem;
};

export const ConfigurationItemBadge = ({ item }: Props) => {
    // A target is worth nothing to a reader as the id it is stored as, so it waits rather than
    // showing what is known so far.
    if (item.isResolving) {
        return <Skeleton className={styles.resolving} />;
    }

    const isSound = item.icon === 'sound';

    const content = (
        <>
            {isSound && <SoundCover imageUrl={item.imageUrl} />}
            {item.countryCode && <FlagIcon countryCode={item.countryCode} />}
            <ConfigurationItemGlyph icon={item.icon} />

            {/* A label with a figure beside it names that figure rather than being the badge's own
                subject, so it steps back and lets the figure carry the contrast. */}
            <span className={cn({ [styles.qualifier]: Boolean(item.value) })}>{item.label}</span>
            {item.value && <span>{item.value}</span>}
        </>
    );

    const className = cn(styles.badge, {
        [styles.target]: item.kind === 'target',
        [styles.withCover]: isSound,
    });

    if (!item.href) {
        return (
            <Badge variant={KIND_VARIANTS[item.kind]} className={className}>
                {content}
            </Badge>
        );
    }

    return (
        <Badge asChild variant={KIND_VARIANTS[item.kind]} className={className}>
            <a href={item.href} target="_blank" rel="noopener noreferrer">
                {content}
            </a>
        </Badge>
    );
};

/**
 * What a badge leads with, on its own — for a row too narrow for the badge, where the icons still
 * have to be the ones the card opens onto.
 */
export const ConfigurationItemGlyph = ({ icon }: { icon: ConfigurationItemIcon | undefined }) => {
    if (!icon || icon === 'sound') {
        return null;
    }

    const Icon = ITEM_ICONS[icon];
    return <Icon aria-hidden="true" />;
};

/**
 * The artwork a sound is recognised by. It carries no alt text: the title it sits beside is the same
 * thing said in words, and a reader hearing both would only hear it twice.
 */
const SoundCover = ({ imageUrl }: { imageUrl: string | undefined }) => (
    <Avatar className={styles.cover}>
        <Avatar.Image src={imageUrl} alt="" />
        <Avatar.Fallback className={styles.coverFallback}>
            <lucideIcons.Music aria-hidden="true" />
        </Avatar.Fallback>
    </Avatar>
);
